import { validateLocalDataLayerPayload as validateDataLayerPayload } from './local-data-layer-limits.js';
import { DATA_CHUNKS_STORE, readLayerRecords, writeLayerRecords, writeDataRecords, removeDataRecords, withDataStoreLock } from './data-layer-records.js';
import { dataLayerPayload, randomDataId, type DataLayer } from '../shared/data-layer.js';
import { z } from 'zod';

const DATABASE_NAME = 'drawonamap-data-drafts';
const STORE_NAME = 'layers';
const DATABASE_VERSION = 2;
export const DATA_LAYER_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredDraft {
  id: string;
  expiresAt: number;
  payload?: ReturnType<typeof dataLayerPayload>;
  manifest?: unknown;
}

export class DataLayerDraftStore {
  constructor(private readonly indexedDb: IDBFactory | undefined = globalThis.indexedDB) {}

  available(): boolean { return Boolean(this.indexedDb); }

  private open(): Promise<IDBDatabase> {
    if (!this.indexedDb) return Promise.reject(new Error('Local data storage is not available in this browser.'));
    return new Promise((resolve, reject) => {
      const request = this.indexedDb!.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(DATA_CHUNKS_STORE)) request.result.createObjectStore(DATA_CHUNKS_STORE, { keyPath: 'id' });
        if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('Could not open local data storage.'));
    });
  }

  put(layer: DataLayer, now = Date.now()): Promise<string> {
    return withDataStoreLock(DATABASE_NAME, () => this.putUnlocked(layer, now));
  }

  private async putUnlocked(layer: DataLayer, now: number): Promise<string> {
    const id = layer.draftId ?? randomDataId('draft');
    const record: StoredDraft = { id, expiresAt: now + DATA_LAYER_DRAFT_TTL_MS, payload: dataLayerPayload(layer) };
    const db = await this.open();
    const created: string[] = [];
    try {
      if (layer.dataset || layer.retainedSources?.length || layer.features.length > 10000) {
        const stored = await writeLayerRecords(db, layer, record.expiresAt, created);
        await writeDataRecords(db, STORE_NAME, [{ ...stored, id }]);
      } else await transactionPromise(db, 'readwrite', (store) => store.put(record));
    } catch (error) {
      await removeDataRecords(db, created);
      throw error;
    } finally { db.close(); }
    return id;
  }

  async get(id: string, now = Date.now()): Promise<DataLayer | null> {
    const db = await this.open();
    const record = await requestPromise<StoredDraft | undefined>(db.transaction(STORE_NAME).objectStore(STORE_NAME).get(id));
    if (record?.manifest && record.expiresAt > now) {
      try {
        const layer = await readLayerRecords(db, record.manifest);
        return layer ? { ...layer, draftId: id } : null;
      } finally { db.close(); }
    }
    db.close();
    if (!record || record.expiresAt <= now) {
      if (record) await this.delete(id);
      return null;
    }
    const validation = validateDataLayerPayload(record.payload);
    if (!validation.success) return null;
    return { ...validation.payload.layer, saved: false, draftId: id, features: validation.payload.features };
  }

  async delete(id: string): Promise<void> {
    const db = await this.open();
    await transactionPromise(db, 'readwrite', (store) => store.delete(id));
    db.close();
  }

  async cleanup(now = Date.now()): Promise<number> {
    const db = await this.open();
    const records = await requestPromise<StoredDraft[]>(db.transaction(STORE_NAME).objectStore(STORE_NAME).getAll());
    const expired = records.filter((record) => record.expiresAt <= now);
    if (expired.length) await transactionPromise(db, 'readwrite', (store) => expired.forEach((record) => store.delete(record.id)));
    db.close();
    return expired.length;
  }
}

interface NavigationDraftState { dataLayers: DataLayer[] }

export async function saveNavigationDataDrafts(state: NavigationDraftState, storageKey: string): Promise<void> {
  const store = new DataLayerDraftStore();
  const referenceKey = `${storageKey}:data-layers`;
  if (!state.dataLayers.length) { sessionStorage.removeItem(referenceKey); return; }
  if (!store.available()) throw new Error('IndexedDB is unavailable');
  const ids: string[] = [];
  for (const layer of state.dataLayers) {
    const id = await store.put(layer);
    layer.draftId = id;
    ids.push(id);
  }
  sessionStorage.setItem(referenceKey, JSON.stringify(ids));
}

export async function restoreNavigationDataDrafts(state: NavigationDraftState, storageKey: string, onLoaded?: (failedCount: number) => void): Promise<boolean> {
  const referenceKey = `${storageKey}:data-layers`;
  const references = sessionStorage.getItem(referenceKey);
  if (!references) return false;
  sessionStorage.removeItem(referenceKey);
  const store = new DataLayerDraftStore();
  try {
    const ids = z.array(z.string()).parse(JSON.parse(references));
    const layers = (await Promise.all(ids.map((id) => store.get(id)))).filter((layer) => layer !== null);
    if (layers.length) { state.dataLayers = layers; onLoaded?.(ids.length - layers.length); }
    void store.cleanup().catch((error) => console.warn('Data draft cleanup failed:', error));
    return layers.length > 0;
  } catch (error) {
    console.error('Failed to restore local data drafts:', error);
    onLoaded?.(1);
    return false;
  }
}

function requestPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Local data storage request failed.'));
  });
}

function transactionPromise(db: IDBDatabase, mode: IDBTransactionMode, write: (store: IDBObjectStore) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    write(transaction.objectStore(STORE_NAME));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('Local data storage transaction failed.'));
    transaction.onabort = () => reject(transaction.error ?? new Error('Local data storage transaction was stopped.'));
  });
}
